import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const milestones = [
  { year: '1993', icon: 'ri-seedling-line', titleKey: 'about.m1_title', descKey: 'about.m1_desc' },
  { year: '2005', icon: 'ri-award-line', titleKey: 'about.m2_title', descKey: 'about.m2_desc' },
  { year: '2019', icon: 'ri-building-line', titleKey: 'about.m3_title', descKey: 'about.m3_desc' },
  { year: '2024', icon: 'ri-rocket-line', titleKey: 'about.m4_title', descKey: 'about.m4_desc' },
];

const values = [
  { icon: 'ri-medal-line', titleKey: 'about.v1_title', descKey: 'about.v1_desc' },
  { icon: 'ri-time-line', titleKey: 'about.v2_title', descKey: 'about.v2_desc' },
  { icon: 'ri-heart-line', titleKey: 'about.v3_title', descKey: 'about.v3_desc' },
  { icon: 'ri-lightbulb-line', titleKey: 'about.v4_title', descKey: 'about.v4_desc' },
];

const stats = [
  { value: '30+', labelKey: 'about.stat1' },
  { value: '5+', labelKey: 'about.stat2' },
  { value: '1000+', labelKey: 'about.stat3' },
  { value: '100%', labelKey: 'about.stat4' },
];

const AboutPage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans pt-20">

      {/* Hero */}
      <section className="relative min-h-[480px] md:min-h-[560px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/d85f4bc4376445be302dc07acb5d206e.jpg"
          alt="Printven LLC equipo"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/75 via-[#0A1628]/60 to-[#0A1628]/80"></div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="inline-block bg-[#FF6B35]/20 text-[#FF6B35] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 border border-[#FF6B35]/30">
            {t('about.badge')}
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
            {t('about.hero_title')}
          </h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed">
            {t('about.hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0A1628] py-12">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.labelKey} className="text-center">
              <p className="text-4xl md:text-5xl font-extrabold text-[#FF6B35]">{s.value}</p>
              <p className="text-white/60 text-sm mt-1">{t(s.labelKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">{t('about.story_badge')}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] mt-3 mb-6 leading-tight">
              {t('about.story_title')}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-4">{t('about.story_p1')}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-4">{t('about.story_p2')}</p>
            <p className="text-gray-600 text-base leading-relaxed">{t('about.story_p3')}</p>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=close%20up%20of%20high%20quality%20custom%20printed%20t-shirts%20and%20apparel%20with%20vibrant%20DTF%20prints%2C%20professional%20product%20photography%2C%20clean%20white%20background%2C%20colorful%20designs%2C%20premium%20quality%20garments&width=640&height=520&seq=about-story-01&orientation=portrait"
              alt="Productos Printven"
              className="w-full h-[420px] object-cover object-top rounded-2xl"
            />
            <div className="absolute -bottom-5 -left-5 bg-[#FF6B35] text-white rounded-2xl px-6 py-4 hidden md:block">
              <p className="text-3xl font-extrabold">30+</p>
              <p className="text-sm font-medium opacity-90">{t('about.years_exp')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">{t('about.timeline_badge')}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] mt-3">{t('about.timeline_title')}</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-[#FF6B35]/20 hidden md:block"></div>
            <div className="flex flex-col gap-10">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex flex-col md:flex-row items-center gap-6 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={`flex-1 w-full ${i % 2 !== 0 ? 'md:text-left' : 'md:text-right'}`}>
                    <div className={`bg-white rounded-2xl p-6 border border-gray-100 ${i % 2 !== 0 ? 'md:ml-8' : 'md:mr-8'}`}>
                      <span className="text-[#FF6B35] font-extrabold text-lg">{m.year}</span>
                      <h3 className="text-[#0A1628] font-bold text-base mt-1 mb-2">{t(m.titleKey)}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{t(m.descKey)}</p>
                    </div>
                  </div>
                  <div className="relative z-10 w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-full flex-shrink-0">
                    <i className={`${m.icon} text-white text-xl`}></i>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">{t('about.values_badge')}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] mt-3">{t('about.values_title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.titleKey} className="bg-white border border-gray-100 rounded-2xl p-7 text-center hover:border-[#FF6B35]/30 transition-colors">
                <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B35]/10 rounded-2xl mx-auto mb-4">
                  <i className={`${v.icon} text-[#FF6B35] text-2xl`}></i>
                </div>
                <h3 className="text-[#0A1628] font-bold text-base mb-2">{t(v.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(v.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team photo */}
      <section className="py-20 px-4 bg-[#0A1628]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-widest">{t('about.commitment_badge')}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mt-3 mb-6 leading-tight">
              {t('about.commitment_title')}
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-6">{t('about.commitment_p1')}</p>
            <p className="text-white/65 text-base leading-relaxed mb-8">{t('about.commitment_p2')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://wa.me/17542034748"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#1ebe5a] transition-colors"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                {t('cta.whatsapp')}
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center gap-2 bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#e85a24] transition-colors"
              >
                {t('cta.quote')}
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20custom%20embroidered%20caps%20and%20hats%20with%20brand%20logos%2C%20product%20photography%2C%20clean%20background%2C%20high%20quality%20printing%2C%20vibrant%20colors%2C%20commercial%20merchandise&width=320&height=320&seq=about-team-01&orientation=squarish"
              alt="Gorras personalizadas"
              className="w-full h-48 object-cover object-top rounded-2xl"
            />
            <img
              src="https://readdy.ai/api/search-image?query=custom%20printed%20promotional%20mugs%20and%20bottles%20with%20colorful%20sublimation%20designs%2C%20product%20photography%2C%20white%20background%2C%20professional%20quality%20merchandise&width=320&height=320&seq=about-team-02&orientation=squarish"
              alt="Tazas y botellas"
              className="w-full h-48 object-cover object-top rounded-2xl mt-6"
            />
            <img
              src="https://readdy.ai/api/search-image?query=DTF%20heat%20transfer%20printing%20process%20on%20fabric%2C%20professional%20printing%20equipment%2C%20vibrant%20colorful%20prints%20on%20white%20t-shirt%2C%20industrial%20printing%20machine&width=320&height=320&seq=about-team-03&orientation=squarish"
              alt="Impresión DTF"
              className="w-full h-48 object-cover object-top rounded-2xl -mt-6"
            />
            <img
              src="https://readdy.ai/api/search-image?query=commercial%20flyers%20and%20business%20cards%20printing%2C%20professional%20graphic%20design%20materials%2C%20colorful%20marketing%20materials%2C%20clean%20white%20background%2C%20high%20quality%20print&width=320&height=320&seq=about-team-04&orientation=squarish"
              alt="Material comercial"
              className="w-full h-48 object-cover object-top rounded-2xl"
            />
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
